/**
 * The MAC table tab: who is behind the ONU, from both sides that can answer.
 *
 * The OLT learns a MAC address per VLAN on the ONU's GEM ports — on a bridged
 * unit those are the subscriber's devices, on a routed one the router's own
 * WAN interfaces. The router, where there is one on the ACS, reports the hosts
 * on its LAN: hostname, address, interface, whether it is still active — and
 * on a bridged unit it reports nothing, because the traffic never enters it.
 * One table joins the two by MAC so a device appears once, with whatever
 * each side knows about it (operator, 2026-10-16: "add a mac table tab").
 */
import type { ConnectedHost } from '@simpleacs/shared';

export interface LearnedMacRow {
  mac: string;
  vlanId: number | null;
  ontPort: string | null;
  vendor?: string | null;
  randomised?: boolean;
}

export interface MacTableRow {
  /** Lower-case, colon-separated. */
  mac: string;
  /** The IEEE assignment, from the OLT side; a hostname from the router side wins where it has one. */
  device: string | null;
  /** The address was made up by the device (a phone's private address); it belongs to nobody. */
  randomised: boolean;
  vlanId: number | null;
  /** Where the OLT saw it: a vport on a ZTE, a socket or radio on a Huawei. */
  ontPort: string | null;
  /** What the router knows, when it reports the host. */
  host: { hostname: string | null; ip: string | null; iface: string | null; active: boolean } | null;
  seenBy: 'olt' | 'router' | 'both';
}

/** `6cd2.a2d1.45c9`, `08cc-8118-1b5c`, `6C:D2:A2:D1:45:C9` → `6c:d2:a2:d1:45:c9`; null when it is not a MAC. */
export function normaliseMac(raw: string | null | undefined): string | null {
  const hex = (raw ?? '').replace(/[^0-9a-f]/gi, '').toLowerCase();
  if (hex.length !== 12) return null;
  return hex.match(/.{2}/g)!.join(':');
}

export function macTableRows(macs: LearnedMacRow[], hosts: ConnectedHost[]): MacTableRow[] {
  const byMac = new Map<string, MacTableRow>();
  for (const m of macs) {
    const mac = normaliseMac(m.mac);
    if (!mac) continue;
    const have = byMac.get(mac);
    if (have) {
      // The same card on two VLANs reads as two rows on the chassis; keep the
      // first, note the other VLAN only if the first had none.
      if (have.vlanId === null && m.vlanId !== null) have.vlanId = m.vlanId;
      continue;
    }
    byMac.set(mac, {
      mac,
      device: m.vendor ?? null,
      randomised: m.randomised === true,
      vlanId: m.vlanId,
      ontPort: m.ontPort,
      host: null,
      seenBy: 'olt',
    });
  }
  for (const h of hosts) {
    const mac = normaliseMac(h.mac);
    if (!mac) continue;
    const host = { hostname: h.hostname?.trim() || null, ip: h.ip?.trim() || null, iface: h.iface?.trim() || null, active: h.active };
    const have = byMac.get(mac);
    if (have) {
      have.host = host;
      have.seenBy = 'both';
      if (host.hostname) have.device = host.hostname;
      continue;
    }
    byMac.set(mac, { mac, device: host.hostname, randomised: false, vlanId: null, ontPort: null, host, seenBy: 'router' });
  }
  return [...byMac.values()].sort((a, b) => {
    const va = a.vlanId ?? Number.MAX_SAFE_INTEGER;
    const vb = b.vlanId ?? Number.MAX_SAFE_INTEGER;
    if (va !== vb) return va - vb;
    return a.mac.localeCompare(b.mac);
  });
}

/** "3 on the OLT, 1 more from the router" — the tab's count line. */
export function macTableSummary(rows: MacTableRow[]): string {
  const olt = rows.filter((r) => r.seenBy !== 'router').length;
  const routerOnly = rows.filter((r) => r.seenBy === 'router').length;
  if (rows.length === 0) return 'No MAC address learned behind this ONU.';
  const parts = [`${olt} learned by the OLT`];
  if (routerOnly > 0) parts.push(`${routerOnly} more reported by the router only`);
  return parts.join(', ');
}
